import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useClient } from 'urql';
import { Download, FileSpreadsheet, Receipt, RefreshCw } from 'lucide-react';
import PageHeader from '@/components/common/PageHeader.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { exportCsv } from '@/utils/exportCsv.js';
import { Q_PURCHASES } from '@/graphql/purchases.gql.js';
import { fetchFacturaDetail, fetchFacturas } from '@/services/facturacion.js';

const API_BASE = import.meta.env.VITE_API_URL || '';
const PAGE_SIZE = 250;
const IVA_CODE_BY_RATE = {
  0: '0003',
  2.5: '0009',
  5: '0008',
  10.5: '0004',
  21: '0005',
  27: '0006',
};
const INVOICE_TYPE_CODE_BY_CLASS = {
  A: { factura: '001', notaCredito: '003', notaDebito: '002' },
  B: { factura: '006', notaCredito: '008', notaDebito: '007' },
  C: { factura: '011', notaCredito: '013', notaDebito: '012' },
  M: { factura: '051', notaCredito: '053', notaDebito: '052' },
};

function resolveTenantCode(tenant) {
  return tenant?.code || tenant?.codigo || tenant?.tenantCode || tenant?.id || tenant?.nombre || '';
}

function buildHeaders({ tenant, token } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const tenantCode = resolveTenantCode(tenant);
  if (tenantCode) headers['X-Tenant'] = tenantCode;
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function fetchPuntosVenta({ tenant, token, sucursalId }) {
  const params = new URLSearchParams();
  if (sucursalId) params.set('sucursal_id', sucursalId);
  params.set('activo', 'true');
  const response = await fetch(`${API_BASE}/api/puntos-venta?${params.toString()}`, {
    headers: buildHeaders({ tenant, token }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) {
    throw new Error(payload?.error || payload?.message || 'No se pudieron cargar los puntos de venta.');
  }
  return Array.isArray(payload.data) ? payload.data : [];
}

function normalizeText(value, length) {
  const base = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9 ./,&()-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
  return base.slice(0, length).padEnd(length, ' ');
}

function onlyDigits(value) {
  return String(value || '').replace(/\D+/g, '');
}

function padDigits(value, length) {
  return onlyDigits(value).slice(-length).padStart(length, '0');
}

function padAlphaNum(value, length) {
  return String(value || '').slice(0, length).padEnd(length, ' ');
}

function amountToArca(value, length = 15) {
  const normalized = Number(value || 0);
  const cents = Math.round(normalized * 100);
  return String(Math.max(cents, 0)).padStart(length, '0');
}

function dateToArca(value) {
  if (!value) return '00000000';
  const digits = String(value).replace(/\D+/g, '');
  if (digits.length >= 8) return digits.slice(0, 8);
  return '00000000';
}

function exchangeRateToArca(value = 1) {
  const scaled = Math.round(Number(value || 1) * 1_000_000);
  return String(Math.max(scaled, 0)).padStart(10, '0');
}

function currencyToArca(value) {
  const code = String(value || 'PES').toUpperCase();
  if (code === 'ARS') return 'PES';
  if (code === 'USD') return 'DOL';
  return padAlphaNum(code, 3);
}

function round2(value) {
  return Math.round(Number(value || 0) * 100) / 100;
}

function inferIvaRateFromAmounts(netAmount, taxAmount) {
  const net = Number(netAmount || 0);
  const tax = Number(taxAmount || 0);
  if (net <= 0 || tax <= 0) return 0;
  const rawRate = (tax / net) * 100;
  const candidates = [2.5, 5, 10.5, 21, 27];
  return candidates.reduce((best, candidate) => (
    Math.abs(candidate - rawRate) < Math.abs(best - rawRate) ? candidate : best
  ), candidates[0]);
}

function inferNetFromTax(taxAmount, rate) {
  const tax = Number(taxAmount || 0);
  const numericRate = Number(rate || 0);
  if (tax <= 0 || numericRate <= 0) return 0;
  return round2(tax / (numericRate / 100));
}

function mapDocumentCodeFromIdentifier(identifier) {
  return onlyDigits(identifier).length >= 11 ? '80' : '99';
}

function mapSalesTypeCode(tipo) {
  const normalized = String(tipo || '').trim().toUpperCase();
  if (normalized === 'A') return INVOICE_TYPE_CODE_BY_CLASS.A.factura;
  if (normalized === 'B') return INVOICE_TYPE_CODE_BY_CLASS.B.factura;
  if (normalized === 'C') return INVOICE_TYPE_CODE_BY_CLASS.C.factura;
  if (normalized === 'M') return INVOICE_TYPE_CODE_BY_CLASS.M.factura;
  return INVOICE_TYPE_CODE_BY_CLASS.A.factura;
}

function mapPurchaseTypeCode(tipo, fallbackClass) {
  const normalized = String(tipo || '').trim().toLowerCase();
  if (normalized === 'factura a') return INVOICE_TYPE_CODE_BY_CLASS.A.factura;
  if (normalized === 'factura b') return INVOICE_TYPE_CODE_BY_CLASS.B.factura;
  if (normalized === 'factura c') return INVOICE_TYPE_CODE_BY_CLASS.C.factura;
  if (normalized === 'factura m') return INVOICE_TYPE_CODE_BY_CLASS.M.factura;
  if (normalized === 'nota de credito') return INVOICE_TYPE_CODE_BY_CLASS[fallbackClass]?.notaCredito || INVOICE_TYPE_CODE_BY_CLASS.A.notaCredito;
  if (normalized === 'nota de debito') return INVOICE_TYPE_CODE_BY_CLASS[fallbackClass]?.notaDebito || INVOICE_TYPE_CODE_BY_CLASS.A.notaDebito;
  return INVOICE_TYPE_CODE_BY_CLASS[fallbackClass]?.factura || INVOICE_TYPE_CODE_BY_CLASS.A.factura;
}

function isWithinRange(value, from, to) {
  if (!value) return false;
  const day = String(value).slice(0, 10);
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

function resolveSalesOperationCode(item) {
  if (Number(item.exento || 0) > 0) return 'E';
  if (Number(item.noGravado || 0) > 0) return 'N';
  return ' ';
}

function buildSalesAliquotRows(item) {
  const detailFactura = item.detailFactura || {};
  const rows = [];
  const knownRates = [
    { rate: 10.5, tax: detailFactura.iva_105 },
    { rate: 21, tax: detailFactura.iva_21 },
    { rate: 27, tax: detailFactura.iva_27 },
  ];

  knownRates.forEach(({ rate, tax }) => {
    const numericTax = round2(tax);
    if (numericTax > 0) {
      rows.push({
        rate,
        neto: inferNetFromTax(numericTax, rate),
        impuesto: numericTax,
        source: 'detail',
      });
    }
  });

  if (!rows.length && Number(item.ivaTotal || 0) > 0) {
    const inferredRate = inferIvaRateFromAmounts(item.subtotal, item.ivaTotal);
    rows.push({
      rate: inferredRate,
      neto: round2(item.subtotal),
      impuesto: round2(item.ivaTotal),
      source: 'inferred',
    });
  }

  return rows;
}

function buildPurchaseAliquotRows(item) {
  const grouped = new Map();
  (item.items || []).forEach((row) => {
    const rate = Number(row.alicuotaIva || 0);
    const key = String(rate);
    if (!grouped.has(key)) {
      grouped.set(key, { rate, neto: 0, impuesto: 0 });
    }
    const current = grouped.get(key);
    current.neto = round2(current.neto + Number(row.subtotal || 0));
    current.impuesto = round2(current.impuesto + Number(row.subtotal || 0) * (rate / 100));
  });

  const rows = [...grouped.values()].filter((row) => row.rate > 0 && row.neto > 0);
  if (rows.length) return rows;

  const fallback = [
    { rate: 10.5, tax: item.importes?.iva105 },
    { rate: 21, tax: item.importes?.iva21 },
    { rate: 27, tax: item.importes?.iva27 },
  ];

  return fallback
    .map(({ rate, tax }) => ({
      rate,
      impuesto: round2(tax),
      neto: inferNetFromTax(tax, rate),
    }))
    .filter((row) => row.impuesto > 0 && row.neto > 0);
}

function buildVentasCabeceraRecord(item) {
  const alicuotas = item.alicuotas || [];
  return [
    dateToArca(item.fecha),
    mapSalesTypeCode(item.tipo),
    padDigits(item.puntoVentaNumero, 5),
    padDigits(item.numeroFiscal, 20),
    padDigits(item.numeroFiscal, 20),
    item.documentoCodigo,
    padDigits(item.documentoNumero, 20),
    normalizeText(item.clienteNombre, 30),
    amountToArca(item.total),
    amountToArca(item.noGravado),
    amountToArca(0),
    amountToArca(item.exento),
    amountToArca(item.percepcionesNacionales),
    amountToArca(item.percepcionesIibb),
    amountToArca(item.percepcionesMunicipales),
    amountToArca(item.impuestosInternos),
    currencyToArca(item.moneda),
    exchangeRateToArca(item.tipoCambio),
    String(alicuotas.length).padStart(1, '0'),
    resolveSalesOperationCode(item),
    amountToArca(item.otrosTributos),
    dateToArca(item.fechaVencimiento),
  ].join('');
}

function buildVentasAlicuotaRecord(item, row) {
  return [
    mapSalesTypeCode(item.tipo),
    padDigits(item.puntoVentaNumero, 5),
    padDigits(item.numeroFiscal, 20),
    amountToArca(row.neto),
    IVA_CODE_BY_RATE[row.rate] || '0003',
    amountToArca(row.impuesto),
  ].join('');
}

function buildComprasCabeceraRecord(item, fallbackClass) {
  const alicuotas = item.alicuotas || [];
  const ivaTotal = round2((item.importes?.iva21 || 0) + (item.importes?.iva105 || 0) + (item.importes?.iva27 || 0));
  const exento = round2(item.importes?.exento || 0);
  const noGravado = round2(item.importes?.noGravado || 0);
  const operationCode = alicuotas.length ? ' ' : exento > 0 ? 'E' : noGravado > 0 ? 'N' : ' ';

  return [
    dateToArca(item.fecha),
    mapPurchaseTypeCode(item.tipo, fallbackClass),
    padDigits(item.puntoVenta, 5),
    padDigits(item.numero, 20),
    ''.padEnd(16, ' '),
    mapDocumentCodeFromIdentifier(item.supplier?.cuit),
    padDigits(item.supplier?.cuit, 20),
    normalizeText(item.supplier?.razonSocial, 30),
    amountToArca(item.importes?.total),
    amountToArca(noGravado),
    amountToArca(exento),
    amountToArca(item.importes?.percepIva),
    amountToArca(0),
    amountToArca(item.importes?.percepIibb),
    amountToArca(0),
    amountToArca(0),
    currencyToArca(item.moneda),
    exchangeRateToArca(1),
    String(alicuotas.length).padStart(1, '0'),
    operationCode,
    amountToArca(ivaTotal),
    amountToArca(item.importes?.otros),
    padDigits('', 11),
    ''.padEnd(30, ' '),
    amountToArca(0),
  ].join('');
}

function buildComprasAlicuotaRecord(item, row, fallbackClass) {
  return [
    mapPurchaseTypeCode(item.tipo, fallbackClass),
    padDigits(item.puntoVenta, 5),
    padDigits(item.numero, 20),
    mapDocumentCodeFromIdentifier(item.supplier?.cuit),
    padDigits(item.supplier?.cuit, 20),
    amountToArca(row.neto),
    IVA_CODE_BY_RATE[row.rate] || '0003',
    amountToArca(row.impuesto),
  ].join('');
}

function downloadTextFile(content, filename) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function summarizeWarnings(ventas) {
  return ventas.reduce((acc, item) => {
    if (item.puntoVentaSource === 'fallback') acc.puntosVentaInferidos += 1;
    if ((item.alicuotas || []).some((row) => row.source === 'inferred')) acc.alicuotasInferidas += 1;
    return acc;
  }, { puntosVentaInferidos: 0, alicuotasInferidas: 0 });
}

function monthStart() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function ExportacionArcaPage() {
  const location = useLocation();
  const toast = useToast();
  const client = useClient();
  const { tenant, token, sucursalActiva } = useAuth();
  const [filters, setFilters] = useState({
    fechaDesde: monthStart(),
    fechaHasta: today(),
    claseFallback: 'A',
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const activeSucursalId = String(sucursalActiva?.id || '');

  const focus = useMemo(() => {
    if (location.pathname.includes('subdiario-iva-ventas')) return 'ventas';
    if (location.pathname.includes('percepciones-emitidas')) return 'percepciones';
    return 'arca';
  }, [location.pathname]);

  const cacheKey = JSON.stringify({
    fechaDesde: filters.fechaDesde,
    fechaHasta: filters.fechaHasta,
    claseFallback: filters.claseFallback,
    sucursal: activeSucursalId,
  });

  const stats = useMemo(() => {
    if (!data) return null;
    const comprasConPercepcion = data.compras.filter((item) => Number(item.importes?.percepIva || 0) > 0 || Number(item.importes?.percepIibb || 0) > 0);
    return {
      ventas: data.ventas.length,
      compras: data.compras.length,
      percepcionesCompras: comprasConPercepcion.length,
      warnings: data.warnings,
    };
  }, [data]);

  const loadExportData = async ({ force = false } = {}) => {
    if (!force && data?.cacheKey === cacheKey) return data;

    setLoading(true);
    try {
      const [facturasList, puntosVentaRows] = await Promise.all([
        fetchFacturas({ tenant, token, sucursalId: activeSucursalId || null }),
        fetchPuntosVenta({ tenant, token, sucursalId: activeSucursalId || null }),
      ]);

      const puntoVentaMap = new Map(
        puntosVentaRows.map((row) => [String(row.id), row.numero]),
      );

      const ventasBase = (facturasList?.items || []).filter((item) => (
        isWithinRange(item.fecha_emision, filters.fechaDesde, filters.fechaHasta)
      ));

      const ventas = await Promise.all(
        ventasBase.map(async (item) => {
          let detailFactura = null;
          try {
            const detail = await fetchFacturaDetail({ tenant, token, id: item.id });
            detailFactura = detail?.factura || null;
          } catch {
            detailFactura = null;
          }

          const numeroFiscal = onlyDigits(detailFactura?.numero || item.numero || item.id);
          const puntoVentaNumero = pointOfSaleNumber(detailFactura, puntoVentaMap);
          const alicuotas = buildSalesAliquotRows({
            subtotal: detailFactura?.subtotal ?? item.subtotal,
            ivaTotal: detailFactura?.iva_total ?? item.iva_total,
            detailFactura,
          });
          const noGravado = alicuotas.length ? 0 : 0;
          const exento = !alicuotas.length && Number(detailFactura?.subtotal ?? item.subtotal ?? 0) > 0 ? Number(detailFactura?.subtotal ?? item.subtotal ?? 0) : 0;
          return {
            id: item.id,
            tipo: detailFactura?.tipo || item.tipo || 'A',
            fecha: detailFactura?.fecha_emision || item.fecha_emision,
            fechaVencimiento: detailFactura?.fecha_vencimiento || detailFactura?.vencimiento_cae || null,
            moneda: detailFactura?.moneda || 'ARS',
            tipoCambio: 1,
            clienteNombre: detailFactura?.cliente_nombre || item.cliente_nombre || `Cliente ${item.cliente_id || ''}`,
            documentoCodigo: mapDocumentCodeFromIdentifier(detailFactura?.cliente_cuit || item.cliente_cuit),
            documentoNumero: detailFactura?.cliente_cuit || item.cliente_cuit || '',
            numeroFiscal,
            puntoVentaNumero,
            puntoVentaSource: puntoVentaNumero === '1' ? 'fallback' : 'resolved',
            subtotal: round2(detailFactura?.subtotal ?? item.subtotal),
            ivaTotal: round2(detailFactura?.iva_total ?? item.iva_total),
            total: round2(detailFactura?.total ?? item.total),
            exento: round2(exento),
            noGravado: round2(noGravado),
            percepcionesNacionales: round2(detailFactura?.percepciones_nacionales || 0),
            percepcionesIibb: round2(detailFactura?.percepciones_iibb || 0),
            percepcionesMunicipales: round2(detailFactura?.percepciones_municipales || 0),
            impuestosInternos: round2(detailFactura?.impuestos_internos || 0),
            otrosTributos: round2(detailFactura?.otros_tributos || 0),
            detailFactura,
            alicuotas,
          };
        }),
      );

      const compras = [];
      let page = 1;
      let totalPages = 1;

      do {
        const result = await client.query(Q_PURCHASES, {
          filter: {
            fechaDesde: filters.fechaDesde || undefined,
            fechaHasta: filters.fechaHasta || undefined,
          },
          pagination: { page, perPage: PAGE_SIZE },
        }).toPromise();

        if (result.error) {
          throw new Error(result.error.message || 'No se pudieron cargar las compras.');
        }

        const payload = result.data?.purchases;
        compras.push(...(payload?.items || []));
        totalPages = Number(payload?.pageInfo?.totalPages || 1);
        page += 1;
      } while (page <= totalPages);

      const normalizedCompras = compras.map((item) => ({
        ...item,
        fecha: item.fecha,
        moneda: item.moneda || 'ARS',
        alicuotas: buildPurchaseAliquotRows(item),
      }));

      const warnings = summarizeWarnings(ventas);
      const payload = {
        cacheKey,
        ventas,
        compras: normalizedCompras,
        warnings,
      };
      setData(payload);
      return payload;
    } finally {
      setLoading(false);
    }
  };

  const handleExportTxt = async (kind) => {
    try {
      const payload = await loadExportData();
      if (!payload) return;

      if (kind === 'ventas-cabecera') {
        if (!payload.ventas.length) {
          toast.info('No hay ventas dentro del rango seleccionado.');
          return;
        }
        downloadTextFile(
          payload.ventas.map(buildVentasCabeceraRecord).join('\r\n'),
          `LIBRO_IVA_DIGITAL_VENTAS_CBTE_${filters.fechaDesde}_${filters.fechaHasta}.txt`,
        );
        toast.success('Exportacion de ventas ARCA generada.');
        return;
      }

      if (kind === 'ventas-alicuotas') {
        const rows = payload.ventas.flatMap((item) => item.alicuotas.map((row) => buildVentasAlicuotaRecord(item, row)));
        if (!rows.length) {
          toast.info('No hay alicuotas de ventas para exportar.');
          return;
        }
        downloadTextFile(
          rows.join('\r\n'),
          `LIBRO_IVA_DIGITAL_VENTAS_ALICUOTAS_${filters.fechaDesde}_${filters.fechaHasta}.txt`,
        );
        toast.success('Exportacion de alicuotas de ventas generada.');
        return;
      }

      if (kind === 'compras-cabecera') {
        if (!payload.compras.length) {
          toast.info('No hay compras dentro del rango seleccionado.');
          return;
        }
        downloadTextFile(
          payload.compras.map((item) => buildComprasCabeceraRecord(item, filters.claseFallback)).join('\r\n'),
          `LIBRO_IVA_DIGITAL_COMPRAS_CBTE_${filters.fechaDesde}_${filters.fechaHasta}.txt`,
        );
        toast.success('Exportacion de compras ARCA generada.');
        return;
      }

      if (kind === 'compras-alicuotas') {
        const rows = payload.compras.flatMap((item) => item.alicuotas.map((row) => buildComprasAlicuotaRecord(item, row, filters.claseFallback)));
        if (!rows.length) {
          toast.info('No hay alicuotas de compras para exportar.');
          return;
        }
        downloadTextFile(
          rows.join('\r\n'),
          `LIBRO_IVA_DIGITAL_COMPRAS_ALICUOTAS_${filters.fechaDesde}_${filters.fechaHasta}.txt`,
        );
        toast.success('Exportacion de alicuotas de compras generada.');
      }
    } catch (error) {
      toast.error(error.message || 'No se pudo generar el archivo ARCA.');
    }
  };

  const handleExportCsv = async (kind) => {
    try {
      const payload = await loadExportData();
      if (!payload) return;

      if (kind === 'subdiario-ventas') {
        if (!payload.ventas.length) {
          toast.info('No hay ventas para exportar.');
          return;
        }
        exportCsv({
          filename: `subdiario-iva-ventas-${filters.fechaDesde}-${filters.fechaHasta}.csv`,
          columns: [
            { label: 'Fecha', key: 'fecha' },
            { label: 'Tipo', key: 'tipo' },
            { label: 'Punto de venta', key: 'puntoVentaNumero' },
            { label: 'Numero', key: 'numeroFiscal' },
            { label: 'Cliente', key: 'clienteNombre' },
            { label: 'CUIT', key: 'documentoNumero' },
            { label: 'Subtotal', key: 'subtotal' },
            { label: 'IVA', key: 'ivaTotal' },
            { label: 'Total', key: 'total' },
          ],
          rows: payload.ventas,
        });
        toast.success('Subdiario IVA ventas exportado.');
        return;
      }

      if (kind === 'percepciones-compras') {
        const rows = payload.compras
          .filter((item) => Number(item.importes?.percepIva || 0) > 0 || Number(item.importes?.percepIibb || 0) > 0)
          .map((item) => ({
            fecha: item.fecha,
            proveedor: item.supplier?.razonSocial || '',
            cuit: item.supplier?.cuit || '',
            tipo: item.tipo,
            puntoVenta: item.puntoVenta,
            numero: item.numero,
            percepIva: item.importes?.percepIva || 0,
            percepIibb: item.importes?.percepIibb || 0,
            otros: item.importes?.otros || 0,
            total: item.importes?.total || 0,
          }));
        if (!rows.length) {
          toast.info('No hay percepciones en compras para exportar.');
          return;
        }
        exportCsv({
          filename: `percepciones-compras-${filters.fechaDesde}-${filters.fechaHasta}.csv`,
          columns: [
            { label: 'Fecha', key: 'fecha' },
            { label: 'Proveedor', key: 'proveedor' },
            { label: 'CUIT', key: 'cuit' },
            { label: 'Tipo', key: 'tipo' },
            { label: 'Punto de venta', key: 'puntoVenta' },
            { label: 'Numero', key: 'numero' },
            { label: 'Percepcion IVA', key: 'percepIva' },
            { label: 'Percepcion IIBB', key: 'percepIibb' },
            { label: 'Otros tributos', key: 'otros' },
            { label: 'Total', key: 'total' },
          ],
          rows,
        });
        toast.success('Percepciones de compras exportadas.');
        return;
      }

      if (kind === 'rg3685-control') {
        const rows = [
          ...payload.ventas.map((item) => ({
            origen: 'VENTA',
            fecha: String(item.fecha || '').slice(0, 10),
            tercero: item.clienteNombre,
            cuit: item.documentoNumero,
            tipo: item.tipo,
            puntoVenta: item.puntoVentaNumero,
            numero: item.numeroFiscal,
            neto: item.subtotal,
            iva: item.ivaTotal,
            total: item.total,
          })),
          ...payload.compras.map((item) => ({
            origen: 'COMPRA',
            fecha: String(item.fecha || '').slice(0, 10),
            tercero: item.supplier?.razonSocial || '',
            cuit: item.supplier?.cuit || '',
            tipo: item.tipo,
            puntoVenta: item.puntoVenta,
            numero: item.numero,
            neto: item.importes?.neto || 0,
            iva: round2((item.importes?.iva21 || 0) + (item.importes?.iva105 || 0) + (item.importes?.iva27 || 0)),
            total: item.importes?.total || 0,
          })),
        ];
        if (!rows.length) {
          toast.info('No hay movimientos para exportar.');
          return;
        }
        exportCsv({
          filename: `rg-3685-control-${filters.fechaDesde}-${filters.fechaHasta}.csv`,
          columns: [
            { label: 'Origen', key: 'origen' },
            { label: 'Fecha', key: 'fecha' },
            { label: 'Tercero', key: 'tercero' },
            { label: 'CUIT', key: 'cuit' },
            { label: 'Tipo', key: 'tipo' },
            { label: 'Punto de venta', key: 'puntoVenta' },
            { label: 'Numero', key: 'numero' },
            { label: 'Neto', key: 'neto' },
            { label: 'IVA', key: 'iva' },
            { label: 'Total', key: 'total' },
          ],
          rows,
        });
        toast.success('Control combinado compras/ventas exportado.');
      }
    } catch (error) {
      toast.error(error.message || 'No se pudo exportar el listado.');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Exportacion ARCA"
        description="Centro fiscal para Libro IVA Digital, subdiarios y listados heredados de Clipper."
        showBreadcrumb
        breadcrumbs={[
          { label: 'Ctas Ctes', href: '/ctasctes' },
          { label: 'Facturacion y Fiscal' },
          { label: 'Exportacion ARCA' },
        ]}
        actions={(
          <button
            type="button"
            onClick={() => loadExportData({ force: true }).then(() => toast.success('Datos fiscales actualizados.')).catch((error) => toast.error(error.message || 'No se pudieron actualizar los datos.'))}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.72)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        )}
      >
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${focus === 'ventas' ? 'bg-[rgba(var(--brand),0.18)] text-[rgb(var(--brand))]' : 'bg-[rgba(var(--panel),0.6)] text-[rgb(var(--muted))]'}`}>
          IVA ventas
        </span>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${focus === 'percepciones' ? 'bg-[rgba(var(--brand),0.18)] text-[rgb(var(--brand))]' : 'bg-[rgba(var(--panel),0.6)] text-[rgb(var(--muted))]'}`}>
          percepciones
        </span>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${focus === 'arca' ? 'bg-[rgba(var(--brand),0.18)] text-[rgb(var(--brand))]' : 'bg-[rgba(var(--panel),0.6)] text-[rgb(var(--muted))]'}`}>
          Libro IVA Digital
        </span>
      </PageHeader>

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.78)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.12)]">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-3">
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[rgba(var(--muted),0.9)]">Fecha desde</label>
            <input
              type="date"
              value={filters.fechaDesde}
              onChange={(event) => setFilters((current) => ({ ...current, fechaDesde: event.target.value }))}
              className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.58)] px-4 py-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            />
          </div>
          <div className="col-span-12 md:col-span-3">
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[rgba(var(--muted),0.9)]">Fecha hasta</label>
            <input
              type="date"
              value={filters.fechaHasta}
              onChange={(event) => setFilters((current) => ({ ...current, fechaHasta: event.target.value }))}
              className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.58)] px-4 py-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            />
          </div>
          <div className="col-span-12 md:col-span-3">
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[rgba(var(--muted),0.9)]">Clase fallback NC/ND compras</label>
            <select
              value={filters.claseFallback}
              onChange={(event) => setFilters((current) => ({ ...current, claseFallback: event.target.value }))}
              className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.58)] px-4 py-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              {['A', 'B', 'C', 'M'].map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div className="col-span-12 md:col-span-3 rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.45)] p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.12em] text-[rgba(var(--muted),0.9)]">Sucursal activa</div>
            <div className="mt-2 text-sm font-semibold text-[rgb(var(--fg))]">{sucursalActiva?.nombre || `#${activeSucursalId || 'sin sucursal'}`}</div>
            <p className="mt-1 text-xs text-[rgba(var(--muted),0.8)]">La exportacion usa el alcance operativo actual para ventas y puntos de venta.</p>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        <SummaryTile title="Ventas" value={stats?.ventas ?? '-'} helper="facturas relevadas" icon={<Receipt className="h-4 w-4" />} />
        <SummaryTile title="Compras" value={stats?.compras ?? '-'} helper="comprobantes relevados" icon={<FileSpreadsheet className="h-4 w-4" />} />
        <SummaryTile title="Percepciones compras" value={stats?.percepcionesCompras ?? '-'} helper="comprobantes con percepciones" icon={<Download className="h-4 w-4" />} />
        <SummaryTile
          title="Alertas"
          value={stats ? `${stats.warnings.puntosVentaInferidos}/${stats.warnings.alicuotasInferidas}` : '-'}
          helper="PV inferidos / alicuotas inferidas"
          icon={<RefreshCw className="h-4 w-4" />}
        />
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <ExportCard
          title="Libro IVA Digital · Ventas"
          description="Genera cabecera y alicuotas del formato actual de ARCA para ventas."
          actions={[
            { label: 'TXT cabecera', onClick: () => handleExportTxt('ventas-cabecera') },
            { label: 'TXT alicuotas', onClick: () => handleExportTxt('ventas-alicuotas') },
            { label: 'CSV subdiario', onClick: () => handleExportCsv('subdiario-ventas'), secondary: true },
          ]}
        />
        <ExportCard
          title="Libro IVA Digital · Compras"
          description="Genera cabecera y alicuotas del formato actual de ARCA para compras."
          actions={[
            { label: 'TXT cabecera', onClick: () => handleExportTxt('compras-cabecera') },
            { label: 'TXT alicuotas', onClick: () => handleExportTxt('compras-alicuotas') },
            { label: 'CSV percepciones', onClick: () => handleExportCsv('percepciones-compras'), secondary: true },
          ]}
        />
        <ExportCard
          title="Clipper · Percepciones"
          description="Listado operativo de percepciones sufridas en compras para auditoria y control interno."
          actions={[
            { label: 'Exportar CSV', onClick: () => handleExportCsv('percepciones-compras') },
          ]}
        />
        <ExportCard
          title="Clipper · Regimen Compras/Ventas"
          description="Vista de control consolidada para contraste interno y preparacion previa a ARCA."
          actions={[
            { label: 'Exportar CSV', onClick: () => handleExportCsv('rg3685-control') },
          ]}
        />
      </section>

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.78)] p-6 text-sm text-[rgba(var(--muted),0.9)] shadow-[0_20px_40px_rgba(var(--shadow),0.12)]">
        <p>Los TXT siguen el diseno vigente de Libro IVA Digital publicado por ARCA. Se generan en ASCII seguro para evitar rechazos por caracteres especiales.</p>
        <p className="mt-2">Si una factura no expone la alicuota discriminada o el punto de venta resoluble, el sistema infiere la mejor opcion disponible con los datos persistidos y lo refleja en la tarjeta de alertas.</p>
      </section>
    </div>
  );
}

function pointOfSaleNumber(detailFactura, puntoVentaMap) {
  const explicitValue = detailFactura?.punto_venta_numero || detailFactura?.punto_venta;
  if (explicitValue) return String(explicitValue);
  const pvId = detailFactura?.punto_venta_id;
  if (pvId != null && puntoVentaMap.has(String(pvId))) {
    return String(puntoVentaMap.get(String(pvId)));
  }
  const parsed = onlyDigits(detailFactura?.numero || '');
  if (parsed.length > 8) {
    return parsed.slice(0, parsed.length - 8);
  }
  return '1';
}

function SummaryTile({ title, value, helper, icon }) {
  return (
    <section className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--panel),0.8)] p-5 shadow-[0_16px_28px_rgba(var(--shadow),0.08)]">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.84)]">{title}</div>
        <div className="text-[rgb(var(--brand))]">{icon}</div>
      </div>
      <div className="mt-3 text-2xl font-semibold text-[rgb(var(--fg))]">{value}</div>
      <p className="mt-1 text-xs text-[rgba(var(--muted),0.78)]">{helper}</p>
    </section>
  );
}

function ExportCard({ title, description, actions }) {
  return (
    <section className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--panel),0.82)] p-6 shadow-[0_18px_34px_rgba(var(--shadow),0.1)]">
      <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">{title}</h2>
      <p className="mt-2 text-sm text-[rgba(var(--muted),0.86)]">{description}</p>
      <div className="mt-5 flex flex-wrap gap-3">
        {actions.map((action) => (
          <button
            key={action.label}
            type="button"
            onClick={action.onClick}
            className={action.secondary
              ? 'rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.64)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))]'
              : 'rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(var(--shadow),0.18)]'}
          >
            {action.label}
          </button>
        ))}
      </div>
    </section>
  );
}
